import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { formatMediumDate } from '../../lib/format.js';
import {
    requiresSigningDate,
    TAX_FORM_OPTIONS,
    TAX_FORM_W8BEN,
    taxFormSummary,
} from '../../lib/tax-form.js';
import { uploadFile } from '../../lib/upload.js';

class FreelancersView extends BaseComponent {
    static properties = {
        _freelancers: { type: Array, state: true },
        _invitations: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _view: { type: String, state: true }, // 'list', 'invite', 'contract', 'external-sow'
        _sowFor: { type: Object, state: true }, // freelancer whose paper SOW is being recorded
        _sow: { type: Object, state: true },
        _sows: { type: Array, state: true }, // SOWs on file for _sowFor
        _contractFor: { type: Object, state: true }, // freelancer whose paperwork is being edited
        _contract: { type: Object, state: true },
        _inviteEmail: { type: String, state: true },
        _inviteMessage: { type: String, state: true },
        _inviteContract: { type: Object, state: true },
        _saving: { type: Boolean, state: true },
        _removingId: { type: String, state: true }, // local users.id awaiting confirmation
    };

    static styles = css`
        :host { display: block; }
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        table { font-size: 0.9rem; }
        .section-title { font-weight: 600; font-size: 1.1rem; margin-top: 2rem; margin-bottom: 0.75rem; }
        .section-title:first-of-type { margin-top: 0; }
        .back-link {
            display: inline-flex; align-items: center; gap: 0.25rem;
            color: #666; text-decoration: none; font-size: 0.9rem; margin-bottom: 1rem;
        }
        .back-link:hover { color: #333; }
        .form-panel { max-width: 500px; }
        .status-badge { font-size: 0.75rem; }
        .confirm-prompt { color: #666; font-size: 0.85rem; margin-right: 0.5rem; }
        .paperwork { font-size: 0.85rem; }
        .paperwork-sub { font-size: 0.75rem; }
        .paperwork-missing { color: #dc3545; font-size: 0.8rem; }
        .form-hint { color: #666; font-size: 0.85rem; margin-bottom: 1rem; }
    `;

    constructor() {
        super();
        this._freelancers = [];
        this._invitations = [];
        this._loading = true;
        this._error = null;
        this._view = 'list';
        this._inviteEmail = '';
        this._inviteMessage = '';
        this._inviteContract = {};
        this._saving = false;
        this._removingId = null;
        this._contractFor = null;
        this._contract = {};
        this._sowFor = null;
        this._sow = {};
        this._sows = [];
    }

    async connectedCallback() {
        super.connectedCallback();
        this._loadData();
    }

    async _loadData() {
        this._loading = true;
        this._error = null;
        try {
            const [freelancersData, invitationsData] = await Promise.all([
                api.get('/api/freelancers'),
                api.get('/api/freelancers/invitations'),
            ]);
            this._freelancers = freelancersData.freelancers || [];
            this._invitations = invitationsData.invitations || [];
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    _startInvite() {
        this._view = 'invite';
        this._inviteEmail = '';
        this._inviteMessage = '';
        this._error = null;
    }

    _backToList() {
        this._view = 'list';
        this._error = null;
    }

    async _submitInvite(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            const body = { email: this._inviteEmail };
            if (this._inviteMessage) {
                body.message = this._inviteMessage;
            }
            await api.post('/api/freelancers/invitations', {
                ...body,
                legal_name: this._inviteContract.legal_name || '',
                msa_reference: this._inviteContract.msa_reference || null,
                msa_signed_on: this._inviteContract.msa_signed_on || null,
                msa_upload_id: this._inviteContract.msa_upload_id || null,
                tax_form: this._inviteContract.tax_form || TAX_FORM_W8BEN,
                tax_form_on_file: !!this._inviteContract.tax_form_on_file,
                tax_form_signed_on: this._inviteContract.tax_form_signed_on || null,
            });
            this._inviteContract = {};
            this._view = 'list';
            await this._loadData();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _revokeInvitation(invitation) {
        this._error = null;
        try {
            await api.delete(`/api/freelancers/invitations/${invitation.id}`);
            await this._loadData();
        } catch (err) {
            this._error = err.message;
        }
    }

    async _removeFreelancer(freelancer) {
        this._error = null;
        try {
            await api.delete(`/api/freelancers/${freelancer.id}`);
            this._removingId = null;
            await this._loadData();
        } catch (err) {
            this._error = err.message;
        }
    }

    _startContract(freelancer) {
        this._contractFor = freelancer;
        // Seed the form from what is on record so an edit is a correction, not
        // a re-entry.
        this._contract = { ...(freelancer.contract || {}) };
        this._error = null;
        this._view = 'contract';
    }

    // Recording an SOW signed on paper keeps the per-contractor numbering
    // continuous. It matters more than it looks: NextSOWNumber is MAX+1, so an
    // unrecorded paper SOW No. 1 makes the next system SOW call itself No. 1 and
    // re-charge the onboarding fee the contractor was already paid.
    async _startExternalSOW(freelancer) {
        this._sowFor = freelancer;
        this._sow = {};
        this._sows = [];
        this._error = null;
        this._view = 'external-sow';
        try {
            const data = await api.get(`/api/freelancers/${freelancer.id}/sows`);
            this._sows = data.sows || [];
        } catch {
            // Staff can still record one without the existing list; the server
            // rejects a duplicate number either way.
            this._sows = [];
        }
    }

    async _uploadSOWDocument(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        this._saving = true;
        this._error = null;
        try {
            const upload = await uploadFile(file);
            this._sow = {
                ...this._sow,
                upload_id: upload.upload_id,
                file_name: file.name,
            };
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _saveExternalSOW(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            // Form-encoded, not JSON — the endpoint reads FormValue.
            const form = new FormData();
            form.append('upload_id', this._sow.upload_id);
            if (this._sow.number) {
                form.append('number', String(this._sow.number));
            }
            await api.upload(`/api/freelancers/${this._sowFor.id}/sows/external`, form);
            this._view = 'list';
            this._sowFor = null;
            await this._loadData();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _saveContract(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            await api.put(`/api/freelancers/${this._contractFor.id}/contract`, {
                legal_name: this._contract.legal_name || '',
                msa_reference: this._contract.msa_reference || null,
                msa_signed_on: this._contract.msa_signed_on || null,
                msa_upload_id: this._contract.msa_upload_id || null,
                tax_form: this._contract.tax_form || TAX_FORM_W8BEN,
                tax_form_on_file: !!this._contract.tax_form_on_file,
                tax_form_signed_on: this._contract.tax_form_signed_on || null,
            });
            this._view = 'list';
            this._contractFor = null;
            await this._loadData();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _uploadMSA(e, target) {
        const file = e.target.files?.[0];
        if (!file) return;
        this._saving = true;
        this._error = null;
        try {
            const upload = await uploadFile(file);
            this[target] = {
                ...this[target],
                msa_upload_id: upload.upload_id,
                msa_file_name: file.name,
            };
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    _renderMSAUpload(target) {
        const current = this[target];
        return html`
            <div class="mb-3">
                <label class="form-label">Signed master services agreement <span class="text-danger">*</span></label>
                ${
                    current.msa_upload_id
                        ? html`<div class="form-text text-success">Attached${current.msa_file_name ? html` \u2014 ${current.msa_file_name}` : ''}</div>`
                        : html`<input type="file" class="form-control" accept="application/pdf"
                            @change="${(e) => this._uploadMSA(e, target)}" />`
                }
            </div>
        `;
    }

    // Summarises what is on record: whether an SOW could be signed at all (legal
    // name) and where the contractor's tax form stands.
    _renderPaperwork(f) {
        const c = f.contract;
        if (!c || !c.legal_name) {
            return html`<span class="paperwork-missing">No legal name \u2014 cannot sign an SOW</span>`;
        }
        const summary = taxFormSummary(c);
        return html`<div class="paperwork">${c.legal_name}</div><div class="paperwork-sub"><span class="${summary.className}">${summary.text}</span></div>`;
    }

    // The tax-form block, shared by the invite and contract forms the way
    // _renderMSAUpload is: `target` names the state property to write into.
    //
    // Which form applies follows from the contractor's lane and cannot be
    // derived from anything this app stores, so staff pick it. Offering only a
    // W-8BEN checkbox is what let a US contractor's W-9 be recorded as a W-8BEN
    // with a confident, invented expiry (issue #151).
    _renderTaxForm(target) {
        const current = this[target];
        const form = current.tax_form || TAX_FORM_W8BEN;
        const update = (patch) => {
            this[target] = { ...this[target], ...patch };
        };

        return html`
            <div class="mb-3">
                <label class="form-label">Tax form</label>
                <select class="form-select" @change="${(e) => update({ tax_form: e.target.value })}">
                    ${TAX_FORM_OPTIONS.map(
                        (o) =>
                            html`<option value="${o.value}" ?selected="${form === o.value}">${o.label}</option>`,
                    )}
                </select>
                <div class="form-text">
                    Set by which master services agreement they signed \u2014 the Canadian template
                    requires a W-8BEN, the US template a W-9.
                </div>
            </div>
            <div class="form-check mb-3">
                <input type="checkbox" class="form-check-input" id="${target}-tax-form-on-file"
                    .checked="${!!current.tax_form_on_file}"
                    @change="${(e) => update({ tax_form_on_file: e.target.checked })}" />
                <label class="form-check-label" for="${target}-tax-form-on-file">Tax form on file</label>
            </div>
            ${
                current.tax_form_on_file
                    ? html`
            <div class="mb-3">
                <label class="form-label">
                    Signed on ${requiresSigningDate(form) ? html`<span class="text-danger">*</span>` : ''}
                </label>
                <input type="date" class="form-control"
                    .value="${current.tax_form_signed_on || ''}"
                    @input="${(e) => update({ tax_form_signed_on: e.target.value })}" />
                <div class="form-text">
                    ${
                        requiresSigningDate(form)
                            ? 'Required \u2014 a W-8BEN expires on the last day of the third succeeding calendar year after signing.'
                            : 'Optional \u2014 a W-9 does not expire. It stays valid until their name, TIN, or entity classification changes.'
                    }
                </div>
            </div>
            `
                    : ''
            }
        `;
    }

    _renderContract() {
        const f = this._contractFor;
        const set = (field) => (e) => {
            this._contract = { ...this._contract, [field]: e.target.value };
        };

        return html`
            <a class="back-link" href="#" @click="${(e) => {
                e.preventDefault();
                this._view = 'list';
            }}">&larr; Back to freelancers</a>
            <div class="page-header"><h4>Contract paperwork</h4></div>
            <p class="page-subtitle">
                ${f.email} — transcribe these from the signed master services agreement. The
                legal name and MSA date appear in every Statement of Work this freelancer signs.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            <form class="form-panel" @submit="${this._saveContract}">
                <div class="mb-3">
                    <label class="form-label">Legal name <span class="text-danger">*</span></label>
                    <input class="form-control" required
                        .value="${this._contract.legal_name || ''}" @input="${set('legal_name')}" />
                    <div class="form-text">Exactly as it appears in the master services agreement.</div>
                </div>
                <div class="mb-3">
                    <label class="form-label">Master agreement reference</label>
                    <input class="form-control"
                        .value="${this._contract.msa_reference || ''}" @input="${set('msa_reference')}" />
                </div>
                <div class="mb-3">
                    <label class="form-label">Master agreement signed on <span class="text-danger">*</span></label>
                    <input type="date" class="form-control" required
                        .value="${this._contract.msa_signed_on || ''}" @input="${set('msa_signed_on')}" />
                </div>
                ${this._renderMSAUpload('_contract')}
                ${this._renderTaxForm('_contract')}
                <div class="d-flex gap-2">
                    <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                        ${this._saving ? 'Saving\u2026' : 'Save'}
                    </button>
                    <button type="button" class="btn btn-outline-secondary" @click="${() => {
                        this._view = 'list';
                    }}">Cancel</button>
                </div>
            </form>
        `;
    }

    _renderExternalSOW() {
        const f = this._sowFor;
        const nextNumber = this._sows.reduce((max, s) => Math.max(max, s.number), 0) + 1;

        return html`
            <a class="back-link" href="#" @click="${(e) => {
                e.preventDefault();
                this._view = 'list';
            }}">&larr; Back to freelancers</a>
            <div class="page-header"><h4>Record a signed Statement of Work</h4></div>
            <p class="page-subtitle">
                ${f.email} — for an SOW signed on paper, outside the system. Recording it keeps this
                freelancer's numbering continuous, so the next SOW generated here does not repeat a
                number that is already signed and re-charge the onboarding fee.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            ${
                this._sows.length > 0
                    ? html`<div class="mb-3">
                <div class="text-muted small">Already on file</div>
                <div>${this._sows.map((s) => html`<span class="badge bg-light text-dark me-1">No. ${s.number} — ${s.status}</span>`)}</div>
            </div>`
                    : nothing
            }

            <form class="form-panel" @submit="${this._saveExternalSOW}">
                <div class="mb-3">
                    <label class="form-label">SOW number</label>
                    <input type="number" min="1" class="form-control" placeholder="${nextNumber}"
                        .value="${this._sow.number || ''}"
                        @input="${(e) => {
                            this._sow = { ...this._sow, number: e.target.value };
                        }}" />
                    <div class="form-text">
                        Leave blank to use the next number (${nextNumber}). Set it to match what the
                        signed document itself says — a paper SOW No. 1 must be recorded as No. 1.
                    </div>
                </div>
                <div class="mb-3">
                    <label class="form-label">Signed document <span class="text-danger">*</span></label>
                    ${
                        this._sow.upload_id
                            ? html`<div class="form-text text-success">Attached${this._sow.file_name ? html` — ${this._sow.file_name}` : ''}</div>`
                            : html`<input type="file" class="form-control" accept="application/pdf"
                                @change="${(e) => this._uploadSOWDocument(e)}" />`
                    }
                    <div class="form-text">The freelancer can download this from their own SOW list.</div>
                </div>
                <div class="d-flex gap-2">
                    <button type="submit" class="btn btn-primary" ?disabled="${this._saving || !this._sow.upload_id}">
                        ${this._saving ? 'Saving…' : 'Record SOW'}
                    </button>
                    <button type="button" class="btn btn-outline-secondary" @click="${() => {
                        this._view = 'list';
                    }}">Cancel</button>
                </div>
            </form>
        `;
    }

    _renderList() {
        const pendingInvitations = this._invitations.filter((i) => i.status === 'pending');
        const pastInvitations = this._invitations.filter((i) => i.status !== 'pending');

        return html`
            <div class="page-header">
                <h4>Freelancers</h4>
                <button class="btn btn-primary btn-sm" @click="${this._startInvite}">+ Invite Freelancer</button>
            </div>
            <p class="page-subtitle">Manage freelance artists and their access.</p>

            ${
                this._loading
                    ? html`
                <div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>
            `
                    : html`
                <h6 class="section-title">Active Freelancers</h6>
                ${
                    this._freelancers.length > 0
                        ? html`
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Joined</th>
                                <th>Paperwork</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this._freelancers.map(
                                (f) => html`
                                <tr>
                                    <td>${f.first_name || ''} ${f.last_name || ''} ${!f.first_name && !f.last_name ? f.username || '-' : ''}</td>
                                    <td>${f.email}</td>
                                    <td class="text-muted">${formatMediumDate(f.created_at)}</td>
                                    <td>${this._renderPaperwork(f)}</td>
                                    <td class="text-end">
                                        <button class="btn btn-outline-secondary btn-sm me-1" @click="${() => this._startContract(f)}">Contract</button>
                                        <button class="btn btn-outline-secondary btn-sm me-1" @click="${() => this._startExternalSOW(f)}">Record SOW</button>
                                        ${
                                            this._removingId === f.id
                                                ? html`
                                            <span class="confirm-prompt">Revoke their access to D3 Assets?</span>
                                            <button class="btn btn-danger btn-sm" @click="${() => this._removeFreelancer(f)}">Remove</button>
                                            <button class="btn btn-outline-secondary btn-sm" @click="${() => {
                                                this._removingId = null;
                                            }}">Cancel</button>
                                        `
                                                : html`
                                            <button class="btn btn-outline-danger btn-sm" @click="${() => {
                                                this._removingId = f.id;
                                            }}">Remove</button>
                                        `
                                        }
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted">No active freelancers yet.</p>`
                }

                <h6 class="section-title">Pending Invitations</h6>
                ${
                    pendingInvitations.length > 0
                        ? html`
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Invited</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${pendingInvitations.map(
                                (inv) => html`
                                <tr>
                                    <td>${inv.email}</td>
                                    <td class="text-muted">${formatMediumDate(inv.created_at)}</td>
                                    <td>
                                        <button class="btn btn-outline-danger btn-sm" @click="${() => this._revokeInvitation(inv)}">Revoke</button>
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted">No pending invitations.</p>`
                }

                ${
                    pastInvitations.length > 0
                        ? html`
                    <h6 class="section-title">Past Invitations</h6>
                    <table class="table">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${pastInvitations.map(
                                (inv) => html`
                                <tr>
                                    <td>${inv.email}</td>
                                    <td>
                                        <span class="badge status-badge ${inv.status === 'accepted' ? 'bg-success' : 'bg-secondary'}">
                                            ${inv.status}
                                        </span>
                                    </td>
                                    <td class="text-muted">${formatMediumDate(inv.accepted_at || inv.created_at)}</td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : nothing
                }
            `
            }
        `;
    }

    _renderInvite() {
        return html`
            <a class="back-link" href="#" @click="${(e) => {
                e.preventDefault();
                this._backToList();
            }}">
                <i class="bi bi-arrow-left"></i> Back to Freelancers
            </a>
            <h4 style="font-weight: 600; margin-bottom: 1.5rem;">Invite Freelancer</h4>
            <div class="form-panel">
                <form @submit="${this._submitInvite}">
                    <div class="mb-3">
                        <label class="form-label">Email Address *</label>
                        <input type="email" class="form-control" required placeholder="artist@example.com"
                            .value="${this._inviteEmail}"
                            @input="${(e) => (this._inviteEmail = e.target.value)}">
                    </div>
                    <h6 class="section-title">Contract paperwork</h6>
                    <p class="form-hint">
                        A freelancer cannot be invited without a signed master services agreement.
                        Transcribe these from it while you have it open — the legal name and date
                        appear in every Statement of Work this freelancer signs.
                    </p>
                    <div class="mb-3">
                        <label class="form-label">Legal name <span class="text-danger">*</span></label>
                        <input class="form-control" required placeholder="Exactly as it appears in the agreement"
                            .value="${this._inviteContract.legal_name || ''}"
                            @input="${(e) => {
                                this._inviteContract = {
                                    ...this._inviteContract,
                                    legal_name: e.target.value,
                                };
                            }}" />
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Master agreement signed on <span class="text-danger">*</span></label>
                        <input type="date" class="form-control" required
                            .value="${this._inviteContract.msa_signed_on || ''}"
                            @input="${(e) => {
                                this._inviteContract = {
                                    ...this._inviteContract,
                                    msa_signed_on: e.target.value,
                                };
                            }}" />
                    </div>
                    ${this._renderMSAUpload('_inviteContract')}

                    ${this._renderTaxForm('_inviteContract')}

                    <div class="mb-3">
                        <label class="form-label">Message</label>
                        <textarea class="form-control" rows="3" placeholder="Optional message to include in the invitation email"
                            .value="${this._inviteMessage}"
                            @input="${(e) => (this._inviteMessage = e.target.value)}"></textarea>
                    </div>
                    <div class="d-flex gap-2">
                        <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                            ${this._saving ? 'Sending...' : 'Send Invitation'}
                        </button>
                        <button type="button" class="btn btn-outline-secondary" @click="${this._backToList}">Cancel</button>
                    </div>
                </form>
            </div>
        `;
    }

    render() {
        return html`
            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._view === 'list' ? this._renderList() : ''}
            ${this._view === 'invite' ? this._renderInvite() : ''}
            ${this._view === 'contract' ? this._renderContract() : ''}
            ${this._view === 'external-sow' ? this._renderExternalSOW() : ''}
        `;
    }
}

customElements.define('freelancers-view', FreelancersView);
